import { useRef, useState, useEffect } from "react";
import { getDealerById, getDealersAllowed, getAllDealers } from "@/services/dealer";
import { DEFAULT_ID } from "./utils";

export const useDealers = ({ disabled, searchText, dealerId, hasGroup }) => {
  const timeoutRef = useRef(null);

  const [search, setSearch] = useState(searchText);
  const [loading, setLoading] = useState(false);
  const [dealers, setDealers] = useState([]);

  useEffect(() => {
    if (searchText === search) {
      return;
    }

    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setSearch(searchText);
      timeoutRef.current = null;
    }, 500);
  }, [searchText, search]);

  useEffect(() => {
    if (disabled) {
      return;
    }

    setLoading(true);

    getAllDealers({ page: 1, pageSize: 10, filter: search, hasGroup })
      .then(async response => {
        const items = response?.items || [];
        const needsToIncludeDealer = dealerId !== undefined && dealerId !== null && dealerId !== DEFAULT_ID;
        const needsToFetchDealer = needsToIncludeDealer && items.every(dealer => dealer.id !== dealerId);

        if (needsToFetchDealer) {
          try {
            const dealer = await getDealerById(dealerId);
            if (dealer) {
              items.push(dealer);
            }
          } catch (error) {
            console.error(error);
          }
        }

        setDealers(items);
      })
      .then(() => {
        setLoading(false);
      });
  }, [disabled, search, dealerId, hasGroup]);

  return { loading, dealers };
};

export const useAllowedDealers = ({ disabled, searchText, dealerId, hasGroup }) => {
  const timeoutRef = useRef(null);

  const [search, setSearch] = useState(searchText);
  const [loading, setLoading] = useState(false);
  const [dealers, setDealers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    if (searchText === search) {
      return;
    }

    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setSearch(searchText);
      timeoutRef.current = null;
    }, 500);
  }, [searchText, search]);

  useEffect(() => {
    if (disabled) {
      return;
    }

    setLoading(true);

    getDealersAllowed({ page: 1, pageSize: 10, filter: search, hasGroup })
      .then(async response => {
        const items = response?.items || [];
        let currentTotalCount = response?.totalCount || 0;

        const needsToIncludeDealer = dealerId !== undefined && dealerId !== null && dealerId !== DEFAULT_ID;
        const needsToFetchDealer = needsToIncludeDealer && items.every(dealer => dealer.id !== dealerId);

        if (needsToFetchDealer) {
          try {
            const dealer = await getDealerById(dealerId);
            if (dealer) {
              currentTotalCount += 1;
              items.push(dealer);
            }
          } catch (error) {
            console.error(error);
          }
        }

        if (!search) {
          setTotalCount(currentTotalCount);
        }

        setDealers(items);
      })
      .then(() => {
        setLoading(false);
      });
  }, [disabled, search, dealerId, hasGroup]);

  return { loading, dealers, totalCount };
};
